/** @jsxImportSource @emotion/react */

import { useMutation, useQuery } from '@apollo/client'
import { createSearchParams, useParams, useSearchParams } from 'react-router-dom'
import { useMemo, useState } from 'react'
import { Typography } from '@mui/material'
import { BLOG_POST_MEDIA_PATH_S3 } from 'constants/images'
import { getTableOfContent, validateTableOfContent } from 'components/TableOfContent'
import withPlatformCheck from 'HOCs/withPlatformCheck'
import { findAllPlatforms_findAllPlatforms_items as Platform } from '__generatedTypes__/findAllPlatforms'
import { useImageUpload } from 'utils/saveImageS3'
import { DELETE_SUCCESS_STORY, FIND_ONE_SUCCESS_STORY, UPDATE_SUCCESS_STORY } from 'queries/successStory'
import { findOneSuccessStory, findOneSuccessStoryVariables } from '__generatedTypes__/findOneSuccessStory'
import { PageLoader } from 'components/PageLoader'
import { updateSuccessStory, updateSuccessStoryVariables } from '__generatedTypes__/updateSuccessStory'
import { removeSuccessStory, removeSuccessStoryVariables } from '__generatedTypes__/removeSuccessStory'
import { cmsRoutes, useLink } from 'routes/cmsRoutes'
import { ImageField } from 'components/DropzoneField'
import { QUERY_PARAM, SUCCESS_STORIES_TAB } from 'pagesNew/BlogPages'
import { toast } from 'utils/toast'
import * as classes from '../Create/styles'
import { FormValues } from '../Create/types'
import { BlogPostType, Form, VALIDATE_TABLE_OF_CONTENT_OPTIONS } from '../Create/Form'
import { handleServerErrors, prepareHtmlForSubmit } from '../Create'
import { useGetSimilarPostsInitialValue } from '../Create/hooks'
import { useFiles } from './hooks'

type Props = {
  platform: Platform
}

export const Edit = withPlatformCheck(({ platform }: Props) => {
  const { successStoryId } = useParams()
  const [searchParams] = useSearchParams()
  const [isPlatformMatch, setIsPlatformMatch] = useState(true)
  const sectionId = searchParams.get('sectionId')

  const uploadImage = useImageUpload()

  const { navigate } = useLink()

  const { loading: successStoriesLoading, getInitialValue } = useGetSimilarPostsInitialValue(platform.id, true)

  const { fetchFromLinks, coverFile, isLoading: isLoadingImages } = useFiles()

  const { data, loading } = useQuery<findOneSuccessStory, findOneSuccessStoryVariables>(FIND_ONE_SUCCESS_STORY, {
    variables: {
      id: successStoryId || '',
    },
    onCompleted: async (data) => {
      const successStory = data.findOneSuccessStory

      setIsPlatformMatch(successStory?.platform.id === platform.id)

      const coverLink = successStory?.getCoverImage || ''
      const initialCoverLink = successStory?.coverImage

      await fetchFromLinks(
        {
          imageLink: '',
        },
        {
          imageLink: coverLink,
          initialLink: initialCoverLink,
        }
      )
    },
    skip: !successStoryId,
  })

  const [updateSuccessStory] = useMutation<updateSuccessStory, updateSuccessStoryVariables>(UPDATE_SUCCESS_STORY)
  const [deleteSuccessStory, { loading: isDeleting }] = useMutation<removeSuccessStory, removeSuccessStoryVariables>(
    DELETE_SUCCESS_STORY
  )

  const initialValues: FormValues | null = useMemo(() => {
    const successStory = data?.findOneSuccessStory

    if (!successStory || isLoadingImages || successStoriesLoading) {
      return null
    }

    return {
      author: [successStory.author],
      title: successStory.title,
      contentAsHtml: successStory.storyAsHtml,
      withWhiteImageBG: successStory.withWhiteImageBG,
      featuredArticleDescription: successStory.featuredArticleDescription,
      description: successStory.description,
      slug: successStory.slug,
      coverImage: coverFile && [coverFile],
      fullName: successStory.fullName,
      score: successStory.score,
      tableOfContent: successStory.tableOfContent,
      similarPosts: getInitialValue(
        successStory.studentsLikeYou.map((successStory) => ({
          id: successStory.id,
          title: successStory.title,
        }))
      ),
    }
  }, [loading, isLoadingImages, successStoriesLoading])

  const uploadFile = async (coverImage: ImageField | undefined) => {
    const coverImageFile = coverImage?.isUploaded ? null : coverImage?.file

    const coverImagePath = await (coverImageFile
      ? uploadImage(coverImageFile, BLOG_POST_MEDIA_PATH_S3)
      : Promise.resolve(coverImage?.initialValue))

    return coverImagePath
  }

  const handleSubmit = async (values: FormValues) => {
    try {
      const {
        title,
        description = '',
        featuredArticleDescription,
        slug,
        withWhiteImageBG = false,
        score,
        fullName,
      } = values
      const tableOfContent = getTableOfContent(values.contentAsHtml)
      const { error } = validateTableOfContent(tableOfContent, VALIDATE_TABLE_OF_CONTENT_OPTIONS)

      if (error) {
        return
      }

      const storyAsHtml = await prepareHtmlForSubmit(values)

      const coverImage = await uploadFile(values.coverImage?.[0])

      const authorId = values.author[0].id || ''

      await updateSuccessStory({
        variables: {
          updateSuccessStoryInputFieldName: {
            id: successStoryId,
            title,
            description,
            featuredArticleDescription,
            slug,
            score,
            fullName,
            author: {
              id: authorId,
            },
            storyAsHtml,
            withWhiteImageBG,
            coverImage,
            tableOfContent,
            platform: {
              id: platform.id,
            },
            studentsLikeYou: values.similarPosts?.map(({ id }) => id),
          },
        },
      })

      toast.success('Success story updated successfully')
      if (sectionId) {
        navigate(cmsRoutes.ORGANIZE_EDIT_SUCCESS_STORIES, {
          id: sectionId,
        })

        return
      }

      navigate(
        cmsRoutes.BLOG_PAGES,
        {},
        { searchParams: createSearchParams({ [QUERY_PARAM]: SUCCESS_STORIES_TAB.id }) }
      )
    } catch (error) {
      toast.error('Failed to update Success Story')
      return handleServerErrors(error)
    }
  }

  const handleDelete = async () => {
    try {
      await deleteSuccessStory({
        variables: {
          removeSuccessStoryId: successStoryId || '',
        },
      })

      toast.success('Success story deleted successfully')

      if (sectionId) {
        navigate(cmsRoutes.ORGANIZE_EDIT_SUCCESS_STORIES, {
          id: sectionId,
        })

        return
      }

      navigate(
        cmsRoutes.BLOG_PAGES,
        {},
        { searchParams: createSearchParams({ [QUERY_PARAM]: SUCCESS_STORIES_TAB.id }) }
      )
    } catch (error) {
      toast.error('Failed to delete success story')
    }
  }

  if (loading || successStoriesLoading || isLoadingImages) {
    return <PageLoader />
  }

  if (!isPlatformMatch) {
    return (
      <Typography>
        You are trying to edit success story with different platform (platform in url: {platform.title}, success story
        platform: {data?.findOneSuccessStory?.platform?.title})
      </Typography>
    )
  }

  if (!initialValues) {
    return <Typography>Something went wrong</Typography>
  }

  const isPostUsedInSection = data?.findOneSuccessStory?.isUsedInSection

  return (
    <section css={classes.container}>
      <Form
        isEditing
        initialValues={initialValues}
        blogPostType={BlogPostType.SuccessStory}
        platform={platform}
        entityId={successStoryId}
        onSubmit={handleSubmit}
        onDelete={!isPostUsedInSection ? handleDelete : undefined}
        isDeleting={isDeleting}
      />
    </section>
  )
})
